import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import usePost from "../../Hooks/usePost";
import PasswordInput from "../../Components/FormElements/PasswordInput";
import inputErrorMessage from "../../Utilities/inputErrorMessage";

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const { postData, loading } = usePost();

  // For email validation
  const validateEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  // For password validation (at least 8 chars, 1 uppercase, 1 lowercase, 1 number)
  const validatePassword = (password) => {
    return /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/.test(password);
  };

  // Check if email exists in contacts
  const checkEmail = async () => {
    setError("");
    setSuccess("");
    if (!email || !validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      // Convert email to lowercase before sending to API
      const result = await postData("/auth/check-email", {
        email: email.toLowerCase(),
      });
      console.log("Check email response:", result); // Debug log

      if (result.exists && result.isRegistered) {
        // Email exists and is registered - allow login
        setIsLogin(true);
        setSuccess("Email found. Please enter your password to login.");
      } else if (result.exists && !result.isRegistered) {
        // Email exists but not registered - show registration form
        setIsLogin(false);
        setSuccess(
          "Email found in our contacts. Please set a password to register."
        );
      } else {
        // Email not found in contacts
        setError(
          "Email not found in our contact database. Please contact support."
        );
      }
    } catch (err) {
      console.error("Email check error:", err);
      setError(err.message || "Error checking email");
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate form
    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password");
      return;
    }

    if (!validateEmail(email)) {
      setError(inputErrorMessage.email);
      return;
    }

    try {
      const result = await postData("/auth/login", {
        email: email.toLowerCase(),
        password,
      });

      console.log("Login result:", result);
      // Update auth context with full contact info
      login(result.data.user, result.token);
      setSuccess("Login successful!");

      // Navigate to home page
      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.");
    }
  };

  // Handle registration
  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate form
    if (!email.trim() || !password.trim() || !confirmPassword.trim()) {
      setError("Please fill all required fields");
      return;
    }

    if (!validateEmail(email)) {
      setError(inputErrorMessage.email);
      return;
    }

    if (!validatePassword(password)) {
      setError(inputErrorMessage.passwordSignUp);
      return;
    }

    if (password !== confirmPassword) {
      setError(inputErrorMessage.passwordConfirm);
      return;
    }

    try {
      const result = await postData("/auth/register", {
        email: email.toLowerCase(),
        password,
        confirmPassword,
      });

      console.log("Registration result:", result);
      // Update auth context with full contact info
      login(result.data.user, result.token);
      setSuccess("Registration successful!");

      // Navigate to home page
      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
    }
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setError("");
    setSuccess("");
    setPassword("");
    setConfirmPassword("");
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-whiteBlue to-blue-50">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-xl border border-gray-100">
        {/* Header with logo */}
        <div className="text-center mb-8">
          <div className="mb-2 mx-auto w-16 h-16 flex items-center justify-center bg-lightBlue rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-darkBlue">
            {isLogin ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isLogin ? "Sign in to your account" : "Register a new account"}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 text-sm text-white bg-red-500 rounded-md shadow-sm animate-pulse">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 text-sm text-white bg-green-500 rounded-md shadow-sm">
            {success}
          </div>
        )}

        {/* Email check form - shown initially */}
        {!success && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              checkEmail();
            }}
            className="space-y-4"
          >
            <div>
              <label
                htmlFor="email"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-lightBlue transition-all duration-200"
                placeholder="Enter your email"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 mt-2 text-white bg-darkBlue rounded-md hover:bg-blue-700 focus:outline-none disabled:bg-gray-400 transition-colors duration-200"
            >
              {loading ? "Checking..." : "Continue"}
            </button>
          </form>
        )}

        {/* Login form - shown after successful email check */}
        {isLogin && success && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-lightBlue transition-all duration-200"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <PasswordInput
                id="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                hasError={false}
              />
              <div className="flex justify-end mt-1">
                <button
                  type="button"
                  className="text-sm text-lightBlue hover:text-darkBlue"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 mt-2 text-white bg-darkBlue rounded-md hover:bg-blue-700 focus:outline-none disabled:bg-gray-400 transition-colors duration-200"
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        )}

        {/* Register form - shown after successful email check */}
        {!isLogin && success && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={true}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none bg-gray-50"
              />
            </div>

            <div>
              <PasswordInput
                id="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create password"
                hasError={false}
                errorMessage={inputErrorMessage.passwordSignUp}
              />
              <p className="mt-1 text-xs text-gray-500">
                Password must be at least 8 characters with 1 uppercase, 1
                lowercase, and 1 number
              </p>
            </div>

            <div>
              <PasswordInput
                id="confirmPassword"
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                hasError={
                  password !== confirmPassword && confirmPassword.length > 0
                }
                errorMessage={inputErrorMessage.passwordConfirm}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 mt-2 text-white bg-darkBlue rounded-md hover:bg-blue-700 focus:outline-none disabled:bg-gray-400 transition-colors duration-200"
            >
              {loading ? "Registering..." : "Register"}
            </button>
          </form>
        )}

        {/* Always show toggle between login/register */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-sm text-center text-gray-600">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={toggleAuthMode}
              className="font-medium text-lightBlue hover:text-darkBlue focus:outline-none transition-colors"
            >
              {isLogin ? "Register" : "Login"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
